import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Button from '@/components/common/Button/Button'
import EmailInput from '@components/auth/EmailInput/EmailInput'
import Logo from '@components/common/Logo/Logo'

import MotionText from '@/components/common/Motion/MotionText'
import { usePageSetup } from '@/hooks/usePageSetup'

import passwordsAPI from '@/services/api/passwords'

export const PasswordForgotPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('auth.pageTitle.forgotPassword'), { scroll: true, noindex: true })

  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string

    try {
      setIsLoading(true)

      await passwordsAPI.forgot({ email })

      navigate("/password/code", { state: { email } })
    } catch (error: any) {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white p-5 pt-8 pb-8 flex flex-col">
      <div className="space-y-4">
        <Logo />
      </div>

      <div className="flex-1 flex flex-col">
        <div className='h-[2rem] w-1'></div>
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('auth.forgotPasswordTitle')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('auth.forgotPasswordDescription')}
          </MotionText>
        </div>
        <motion.div className='my-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
        >
          <EmailInput
            required
            autoComplete='email'
            name='email'
            label={t('auth.yourEmail')}
          />
        </motion.div>
      </div>

      <div className="flex flex-row gap-4">
        <Button type='submit' isLoading={isLoading}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
          >
            {t('auth.send')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default PasswordForgotPage